import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { loginSchema, totpCodeSchema, type LoginInput } from '@rechnungen/shared';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FaktLogo, Wordmark } from '@/components/brand';
import { useAuth } from '@/lib/auth-context';
import { ApiError } from '@/lib/api';
import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';

const totpSchema = z.object({ code: totpCodeSchema });
type TotpInput = z.infer<typeof totpSchema>;

/**
 * Validiert `returnTo` gegen Open-Redirect: muss eine absolute URL auf dem
 * EIGENEN Origin sein UND exakt auf `/oauth/authorize` zeigen (der einzige
 * Fall, der diesen Query-Param setzt — Backend-Redirect aus
 * routes/mcp-oauth.ts, wenn `/oauth/authorize` ohne gültige Session
 * aufgerufen wurde). Alles andere wird verworfen statt navigiert.
 */
function getSafeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== '/oauth/authorize') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function LoginPage() {
  const { user, login, verify2fa, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState<string | null>(null);

  // 2FA-Schritt: null = nicht aktiv, sonst tempToken
  const [pendingTempToken, setPendingTempToken] = useState<string | null>(null);

  const credForm = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const totpForm = useForm<TotpInput>({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: '' },
  });

  // `returnTo` kommt vom MCP-OAuth-Backend-Redirect (siehe oben) — zeigt auf
  // einen Backend-Endpunkt (`/oauth/authorize`), keine React-Router-Route,
  // daher `window.location.href` statt `navigate()`.
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation ? `${fromLocation.pathname}${fromLocation.search ?? ''}` : '/';

  const goToDestination = () => {
    if (returnTo) {
      window.location.href = returnTo;
      return;
    }
    navigate(from, { replace: true });
  };

  // Bereits eingeloggt → auf Dashboard/returnTo weiterleiten
  if (!loading && user) {
    if (returnTo) {
      window.location.href = returnTo;
      return null;
    }
    return <Navigate to={from} replace />;
  }

  const onCredentialsSubmit = async (values: LoginInput) => {
    setAuthError(null);
    try {
      const result = await login(values.email, values.password);
      if (result.requires2fa) {
        setPendingTempToken(result.tempToken);
        // Code-Feld fokussieren sobald Step 2 gerendert
        setTimeout(() => document.getElementById('totp-code')?.focus(), 50);
      } else {
        goToDestination();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setAuthError(err.message);
      } else {
        setAuthError('Anmeldung fehlgeschlagen');
      }
    }
  };

  const onTotpSubmit = async (values: TotpInput) => {
    if (!pendingTempToken) return;
    setAuthError(null);
    try {
      await verify2fa(pendingTempToken, values.code);
      goToDestination();
    } catch (err) {
      if (err instanceof ApiError) {
        setAuthError(err.message);
        // BL-682: Token abgelaufen oder verbraucht: zurueck zum Passwort-Schritt, statt mit einem toten Token weiter zu raten.
        if (err.code === 'INVALID_TOKEN') setPendingTempToken(null);
      } else {
        setAuthError('2FA-Verifikation fehlgeschlagen');
      }
      totpForm.reset();
      setTimeout(() => document.getElementById('totp-code')?.focus(), 50);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-alt p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center">
          {/* Brand-Hierarchie wie in der Sidebar: avameo (Firma) klein,
               FAKT (Produkt) prominent. Konsistent mit dem App-Icon. */}
          <Wordmark size={18} className="mb-1" />
          <FaktLogo size={36} className="mb-3" />
          {pendingTempToken ? (
            <>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Zwei-Faktor-Authentifizierung
              </CardTitle>
              <CardDescription>
                Gib den 6-stelligen Code aus deiner Authenticator-App ein.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle>Anmelden bei avameo fakt</CardTitle>
              <CardDescription>Rechnungswesen & Controlling der avameo GmbH</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {/* Schritt 1: E-Mail + Passwort */}
          {!pendingTempToken && (
            <form onSubmit={credForm.handleSubmit(onCredentialsSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  {...credForm.register('email')}
                  aria-invalid={Boolean(credForm.formState.errors.email)}
                />
                {credForm.formState.errors.email && (
                  <p className="text-xs text-destructive">
                    {credForm.formState.errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  {...credForm.register('password')}
                  aria-invalid={Boolean(credForm.formState.errors.password)}
                />
                {credForm.formState.errors.password && (
                  <p className="text-xs text-destructive">
                    {credForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {authError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {authError}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={credForm.formState.isSubmitting}>
                {credForm.formState.isSubmitting ? 'Wird angemeldet …' : 'Anmelden'}
              </Button>
            </form>
          )}

          {/* Schritt 2: TOTP-Code */}
          {pendingTempToken && (
            <form onSubmit={totpForm.handleSubmit(onTotpSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="totp-code">Authenticator-Code</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123 456"
                  maxLength={6}
                  {...totpForm.register('code')}
                  aria-invalid={Boolean(totpForm.formState.errors.code)}
                  className="text-center text-xl tracking-widest tabular-nums"
                />
                {totpForm.formState.errors.code && (
                  <p className="text-xs text-destructive">
                    {totpForm.formState.errors.code.message}
                  </p>
                )}
              </div>

              {authError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {authError}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={totpForm.formState.isSubmitting}>
                {totpForm.formState.isSubmitting ? 'Wird geprüft …' : 'Bestätigen'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setPendingTempToken(null);
                  setAuthError(null);
                  totpForm.reset();
                }}
                className="w-full text-center text-xs text-ink-muted underline-offset-2 hover:underline"
              >
                Zurück zur Anmeldung
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
